// SPDX-FileCopyrightText: 2026 LiveKit, Inc.
//
// SPDX-License-Identifier: Apache-2.0
import type { AgentMood } from './mood.js';

/**
 * Weighted keywords per mood: the emotion words from Parrott's hierarchical classification
 * of emotions (2001) mapped onto the moods in `mood.ts`, plus the delivery descriptors
 * providers use in place of naming a feeling ("bright", "hushed", "clipped").
 *
 * Weight 2 names a mood outright; weight 1 is a supporting descriptor that loses to an
 * explicit naming elsewhere in the same label. That is what lets "gently curious,
 * welcoming" resolve to `curious` rather than `empathetic`.
 */
export const MOOD_KEYWORDS: Record<AgentMood, Record<string, number>> = {
  excited: {
    excit: 2,
    elat: 2,
    thrill: 2,
    exhilarat: 2,
    ecstat: 2,
    euphor: 2,
    zeal: 2,
    zest: 2,
    enthusias: 2,
    eager: 2,
    giddy: 2,
    hyped: 2,
    pumped: 2,
    buzzing: 2,
    jubilant: 2,
    jubilation: 2,
    exuberant: 2,
    rapture: 2,
    enthrall: 2,
    triumph: 2,
    gleeful: 2,
    glee: 2,
    punchy: 2,
    upbeat: 1,
    bright: 1,
    energetic: 1,
    energy: 1,
    lively: 1,
    animated: 1,
    vibrant: 1,
    spirited: 1,
    peppy: 1,
    snappy: 1,
    fast: 1,
    loud: 1,
  },
  happy: {
    happy: 2,
    happiness: 2,
    joy: 2,
    joviality: 2,
    jolli: 2,
    cheer: 2,
    glad: 2,
    delight: 2,
    pleas: 2,
    content: 2,
    bliss: 2,
    gaiety: 2,
    enjoy: 2,
    satisf: 2,
    relief: 2,
    relieved: 2,
    grateful: 2,
    thankful: 2,
    affection: 2,
    fond: 2,
    adore: 2,
    proud: 2,
    pride: 2,
    smil: 2,
    sunny: 2,
    merry: 2,
    amiable: 2,
    warm: 1,
    inviting: 1,
    welcom: 1,
    friendly: 1,
    kind: 1,
    pleasant: 1,
    positive: 1,
    easy: 1,
  },
  playful: {
    playful: 2,
    jok: 2,
    comedic: 2,
    comic: 2,
    sarcas: 2,
    teas: 2,
    witty: 2,
    silly: 2,
    goofy: 2,
    mischiev: 2,
    amus: 2,
    humor: 2,
    humour: 2,
    cheeky: 2,
    sassy: 2,
    ironic: 2,
    irony: 2,
    deadpan: 2,
    banter: 2,
    laugh: 2,
    giggl: 2,
    chuckl: 2,
    grin: 2,
    unimpressed: 2,
    smirk: 2,
    wry: 1,
    sly: 1,
    impish: 1,
  },
  curious: {
    curious: 2,
    curiosity: 2,
    inquisitive: 2,
    intrigu: 2,
    wonder: 2,
    quizzical: 2,
    probing: 2,
    interested: 2,
    engaged: 2,
    attentive: 2,
    suspense: 2,
    questioning: 1,
    question: 1,
    prompting: 1,
    exploring: 1,
  },
  surprised: {
    surpris: 2,
    amaz: 2,
    astonish: 2,
    astound: 2,
    awe: 2,
    shock: 2,
    startl: 2,
    incredulous: 2,
    stunned: 2,
    bewilder: 2,
    flabbergast: 2,
    disbelie: 2,
    unexpected: 2,
    dumbfound: 2,
    wow: 2,
    whoa: 2,
    gasp: 2,
  },
  hopeful: {
    hopeful: 2,
    hope: 2,
    optimis: 2,
    encourag: 2,
    uplift: 2,
    inspir: 2,
    motivat: 2,
    promising: 2,
    determined: 2,
    resolute: 2,
    forward: 2,
    buoyant: 2,
    expectant: 2,
    heartened: 2,
    confident: 2,
    assured: 2,
  },
  empathetic: {
    empath: 2,
    sympath: 2,
    compassion: 2,
    concern: 2,
    care: 2,
    tender: 2,
    consol: 2,
    sorry: 2,
    apolog: 2,
    understanding: 2,
    supportive: 2,
    comfort: 2,
    soothing: 2,
    nurtur: 2,
    patient: 2,
    earnest: 2,
    heartfelt: 2,
    sensitive: 2,
    pity: 2,
    condolence: 2,
    sentimental: 2,
    sincere: 1,
    gentle: 1,
    gently: 1,
    soft: 1,
    quiet: 1,
    hushed: 1,
    mild: 1,
    reassur: 1,
  },
  sad: {
    sad: 2,
    sorrow: 2,
    mourn: 2,
    somber: 2,
    sombre: 2,
    melanchol: 2,
    grief: 2,
    griev: 2,
    regret: 2,
    remorse: 2,
    deject: 2,
    downcast: 2,
    gloom: 2,
    glum: 2,
    forlorn: 2,
    wistful: 2,
    disappoint: 2,
    dismay: 2,
    crestfallen: 2,
    despond: 2,
    despair: 2,
    depress: 2,
    anguish: 2,
    agony: 2,
    woe: 2,
    miser: 2,
    unhappy: 2,
    tearful: 2,
    weep: 2,
    heartbroken: 2,
    heartbreak: 2,
    lonely: 2,
    loneliness: 2,
    ashamed: 2,
    shame: 2,
    guilt: 2,
    humiliat: 2,
    mortified: 2,
    longing: 2,
    homesick: 2,
    defeated: 2,
    heavy: 1,
    subdued: 1,
    flat: 1,
    weary: 1,
    resigned: 1,
    hollow: 1,
  },
  angry: {
    angry: 2,
    anger: 2,
    furious: 2,
    fury: 2,
    frustrat: 2,
    annoy: 2,
    irritat: 2,
    irate: 2,
    indignant: 2,
    outrag: 2,
    exasperat: 2,
    incensed: 2,
    livid: 2,
    wrath: 2,
    hostil: 2,
    resent: 2,
    bitter: 2,
    disgust: 2,
    revulsion: 2,
    contempt: 2,
    loathing: 2,
    scorn: 2,
    spite: 2,
    aggravat: 2,
    agitat: 2,
    grouchy: 2,
    grumpy: 2,
    seething: 2,
    fuming: 2,
    scold: 2,
    stern: 2,
    harsh: 2,
    sharp: 1,
    clipped: 1,
    terse: 1,
    cold: 1,
    biting: 1,
    curt: 1,
  },
  anxious: {
    anxious: 2,
    anxiety: 2,
    afraid: 2,
    fear: 2,
    fright: 2,
    scared: 2,
    nervous: 2,
    worri: 2,
    uneasy: 2,
    unease: 2,
    tense: 2,
    apprehensive: 2,
    panic: 2,
    alarm: 2,
    dread: 2,
    terror: 2,
    horror: 2,
    hesitant: 2,
    unsure: 2,
    uncertain: 2,
    timid: 2,
    jittery: 2,
    urgent: 2,
    stress: 2,
    distress: 2,
    insecure: 2,
    flustered: 2,
    cautious: 1,
    wary: 1,
    guarded: 1,
  },
  calm: {
    calm: 2,
    contemplative: 2,
    thoughtful: 2,
    measured: 2,
    serene: 2,
    easygoing: 2,
    neutral: 2,
    relax: 2,
    composed: 2,
    collected: 2,
    tranquil: 2,
    peaceful: 2,
    restrained: 2,
    reflective: 2,
    pensive: 2,
    matter: 2,
    plain: 2,
    professional: 2,
    formal: 2,
    informative: 2,
    factual: 2,
    deliberate: 2,
    unhurried: 2,
    placid: 2,
    slow: 2,
    steady: 1,
    grounded: 1,
    balanced: 1,
    straightforward: 1,
    even: 1,
  },
};
